import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../services/logger.service';
import { Router } from '@angular/router';
import { NavbarComponent } from '../../../../shared/layout/navbar/navbar.component';
import { SidebarComponent } from '../../../../shared/layout/sidebar/sidebar.component';
import { PageHeaderComponent } from '../../../../shared/layout/page-header/page-header.component';
import { ChatComponent } from '../../../shared/components/chat/chat.component';
import {
  CancelAppointmentModalComponent,
  CancelData,
} from '../../../../shared/components/cancel-appointment-modal/cancel-appointment-modal.component';
import {
  RescheduleAppointmentModalComponent,
  RescheduleData,
} from '../../../../shared/components/reschedule-appointment-modal/reschedule-appointment-modal.component';
import { AuthService, AuthUser } from '../../../../core/services/auth.service';
import { HospitalEmployeeRole } from '../../../../core/enums/hospital-employee-role.enum';
import { RoleRoutingService } from '../../../../services/role-routing.service';
import { TranslationService } from '../../../../services/translation.service';
import { ApiService } from '../../../../core/services/api.service';
import { firstValueFrom } from 'rxjs';
import { AppointmentsService } from '../../../../components/schedule/services/appointments.service';
import { AppointmentEvent } from '../../../../core/types/common.types';

export interface Appointment {
  id: string;
  time: string;
  patientName: string;
  status: string;
}

export interface ActivityLog {
  id: string;
  actor_id: string;
  actor_type: string;
  actor_name: string;
  action: string;
  entity_type: string;
  entity_id: string;
  description: string;
  hospital_id: string;
  patient_id: string;
  created_at: string;
}

export interface ActivityLogsResponse {
  items: ActivityLog[];
  total: number;
}

export interface Activity {
  id: string;
  description: string;
  date: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NavbarComponent,
    SidebarComponent,
    PageHeaderComponent,
    ChatComponent,
    CancelAppointmentModalComponent,
    RescheduleAppointmentModalComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  currentUser = signal<AuthUser | null>(null);
  selectedMenu = signal('dashboard');
  isChatOpen = signal(false);

  // Modal states
  isCancelModalOpen = signal(false);
  isRescheduleModalOpen = signal(false);
  selectedAppointment = signal<AppointmentEvent | null>(null);

  // Dashboard data
  todayAppointments = signal<number>(0);
  totalPatients = signal<number>(0);
  criticalCare = signal<number>(0);
  inPatients = signal<number>(0);
  outPatients = signal<number>(0);

  upcomingAppointments = signal<Appointment[]>([]);

  recentActivities = signal<Activity[]>([]);

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly roleRoutingService = inject(RoleRoutingService);
  private readonly apiService = inject(ApiService);
  private readonly appointmentsService = inject(AppointmentsService);
  public readonly translationService = inject(TranslationService);

  ngOnInit() {
    this.currentUser.set(this.authService.currentUser());
    if (!this.currentUser()) {
      this.router.navigate(['/login']);
    }
    this.loadTotalPatients();
    this.loadTodayAppointments();
    this.loadRecentActivities();
    this.loadCriticalCareCount();
    this.loadPatientModeDistribution();
  }

  async loadTodayAppointments() {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    try {
      const hospitalId = user.hospital_id;
      const employeeId = user.hospital_employee_id;

      const appointments = await firstValueFrom(
        this.appointmentsService.getAppointments(hospitalId, employeeId, { today: true })
      );

      this.todayAppointments.set(appointments.length);

      // Convertir les appointments pour upcomingAppointments
      const upcoming = appointments.map((apt) => ({
        id: apt.id,
        time: apt.time,
        patientName: apt.patient,
        status: 'coming up',
      }));

      this.upcomingAppointments.set(upcoming);
    } catch (error) {
      this.logger.error('Error loading today appointments:', error);
      this.todayAppointments.set(0);
      this.upcomingAppointments.set([]);
    }
  }

  async loadTotalPatients() {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    try {
      const hospitalEmployeeId = user.hospital_employee_id;
      const hospitalId = user.hospital_id;

      if (user.role === HospitalEmployeeRole.DOCTOR) {
        // Endpoint pour les médecins
        const response = await firstValueFrom(
          this.apiService.authGet<{
            primary_medic_id: string;
            patient_count: number;
          }>(`secure/primary-medics/${hospitalEmployeeId}/patients/count`)
        );
        this.totalPatients.set(response.patient_count);
      } else if (user.role === HospitalEmployeeRole.NURSE) {
        // Endpoint pour les infirmières
        const response = await firstValueFrom(
          this.apiService.authGet<{
            hospital_id: string;
            patient_count: number;
          }>(`secure/hospitals/${hospitalId}/patients/count`)
        );
        this.totalPatients.set(response.patient_count);
      }
    } catch (error) {
      this.logger.error('Error loading patient count:', error);
      this.totalPatients.set(0);
    }
  }

  async loadRecentActivities() {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    try {
      const employeeId = user.hospital_employee_id;
      const response = await firstValueFrom(
        this.apiService.authGet<ActivityLogsResponse>(
          `secure/activity-logs/actor/${employeeId}?limit=10&offset=0`
        )
      );

      const activities: Activity[] = response.items.map((log) => ({
        id: log.id,
        description: log.description,
        date: new Date(log.created_at).toISOString().split('T')[0],
      }));

      this.recentActivities.set(activities);
    } catch (error) {
      this.logger.error('Error loading recent activities:', error);
      this.recentActivities.set([]);
    }
  }

  async loadCriticalCareCount() {
    try {
      const response = await firstValueFrom(
        this.apiService.authGet<{ criticalCareCount: number }>(
          'secure/dashboard/critical-care-count'
        )
      );
      this.criticalCare.set(response.criticalCareCount);
    } catch (error) {
      this.logger.error('Error loading critical care count:', error);
      this.criticalCare.set(0);
    }
  }

  async loadPatientModeDistribution() {
    try {
      const response = await firstValueFrom(
        this.apiService.authGet<{
          inPatientModeCount: number;
          outPatientModeCount: number;
          total: number;
          inPatientModePercent: number;
          outPatientModePercent: number;
        }>('secure/dashboard/patient-mode-distribution')
      );
      this.inPatients.set(response.inPatientModePercent);
      this.outPatients.set(response.outPatientModePercent);
    } catch (error) {
      this.logger.error('Error loading patient mode distribution:', error);
      this.inPatients.set(0);
      this.outPatients.set(0);
    }
  }

  // Page header event handlers
  onFilterClicked() {
    this.logger.debug('Filter clicked');
    // Implement filter functionality
  }

  // Chat methods
  openChat() {
    this.isChatOpen.set(true);
  }

  closeChat() {
    this.isChatOpen.set(false);
  }

  selectMenu(menu: string) {
    this.selectedMenu.set(menu);
    // Trigger navigation when menu is selected
    this.onMenuSelect(menu);
  }

  onMenuSelect(menu: string) {
    this.selectedMenu.set(menu);

    // Utiliser le service de routing role-based pour une navigation fiable
    const navigated = this.roleRoutingService.navigateToMenu(menu);
    if (!navigated) {
      this.logger.warn('Unable to navigate to menu:', menu);
      // Fallback vers le dashboard si la navigation échoue
      if (menu !== 'dashboard') {
        this.router.navigate(['/medical/dashboard']);
      }
    }
  }

  logout() {
    this.authService.logout().subscribe();
    this.router.navigate(['/login']);
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good Morning';
    }
    if (hour < 18) {
      return 'Good Afternoon';
    }
    return 'Good Evening';
  }

  getRoleDisplayName(): string {
    const user = this.currentUser();
    if (!user) {
      return 'User';
    }

    switch (user.role) {
      case HospitalEmployeeRole.ADMIN:
        return 'Admin';
      case HospitalEmployeeRole.DOCTOR:
        return 'Doctor';
      case HospitalEmployeeRole.NURSE:
        return 'Nurse';
      default:
        return 'User';
    }
  }

  getUserRole(): string {
    const user = this.currentUser();
    return user?.role || 'doctor';
  }

  // Modal management methods
  openCancelModal(appointment: Appointment) {
    this.selectedAppointment.set({
      id: appointment.id,
      title: appointment.patientName,
      date: 'Today',
      time: appointment.time,
      type: 'appointment',
      status: 'scheduled',
    });
    this.isCancelModalOpen.set(true);
  }

  closeCancelModal() {
    this.isCancelModalOpen.set(false);
    this.selectedAppointment.set(null);
  }

  openRescheduleModal(appointment: Appointment) {
    this.selectedAppointment.set({
      id: appointment.id,
      title: appointment.patientName,
      date: 'Today',
      time: appointment.time,
      type: 'appointment',
      status: 'scheduled',
    });
    this.isRescheduleModalOpen.set(true);
  }

  closeRescheduleModal() {
    this.isRescheduleModalOpen.set(false);
    this.selectedAppointment.set(null);
  }

  handleCancelAppointment(cancelData: CancelData) {
    const appointment = this.selectedAppointment();

    if (appointment) {
      this.logger.info('Cancelling appointment with alternative proposal:', {
        appointmentId: appointment.id,
        cancelReason: cancelData.reason,
        proposedDate: cancelData.proposedDate,
        proposedTime: cancelData.proposedTime,
        doctorNote: cancelData.doctorNote,
      });

      // TODO: Implement actual cancel logic with API call
      this.closeCancelModal();
      // Reload appointments after cancellation
      this.loadTodayAppointments();
    }
  }

  handleRescheduleAppointment(rescheduleData: RescheduleData) {
    const appointment = this.selectedAppointment();

    if (appointment) {
      this.logger.info('Rescheduling appointment:', {
        appointmentId: appointment.id,
        newDate: rescheduleData.date,
        newTime: rescheduleData.time,
        reason: rescheduleData.reason,
      });

      // TODO: Implement actual reschedule logic with API call
      this.closeRescheduleModal();
      // Reload appointments after rescheduling
      this.loadTodayAppointments();
    }
  }
}
